"use client";

import { useState, useMemo } from "react";
import Link from "next/link";

export interface BlogCategoryItem {
  id: string;
  slug: string;
  name: string;
}

export interface BlogPostItem {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  coverImage: string | null;
  publishedAt: Date | string | null;
  readingTime: number | null;
  featured?: boolean;
  authorName?: string | null;
  author?: { name: string | null } | null;
  category?: { name: string; slug: string } | null;
}

interface BlogListClientProps {
  posts: BlogPostItem[];
  categories: BlogCategoryItem[];
  emptyState: {
    eyebrow: string;
    text: string;
  };
}

function fmtDate(d: Date | string | null) {
  if (!d) return "";
  const dateObj = typeof d === "string" ? new Date(d) : d;
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(dateObj);
}

export function BlogListClient({ posts, categories, emptyState }: BlogListClientProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [visibleCount, setVisibleCount] = useState<number>(9);

  // Category counts
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: posts.length };
    posts.forEach((p) => {
      if (p.category?.slug) {
        counts[p.category.slug] = (counts[p.category.slug] || 0) + 1;
      }
    });
    return counts;
  }, [posts]);

  // Filtered posts logic
  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      // Category match
      if (selectedCategory !== "all" && post.category?.slug !== selectedCategory) {
        return false;
      }
      // Search match
      if (searchQuery.trim() !== "") {
        const q = searchQuery.toLowerCase();
        const titleMatch = post.title.toLowerCase().includes(q);
        const excerptMatch = post.excerpt?.toLowerCase().includes(q) ?? false;
        const catMatch = post.category?.name.toLowerCase().includes(q) ?? false;
        return titleMatch || excerptMatch || catMatch;
      }
      return true;
    });
  }, [posts, selectedCategory, searchQuery]);

  const hasFilter = searchQuery.trim() !== "" || selectedCategory !== "all";

  // Featured post: If no filter is active, pick the first featured/first post as hero
  const featuredPost = !hasFilter && posts.length > 0 ? posts[0] : null;
  const gridPosts = !hasFilter && featuredPost ? filteredPosts.slice(1) : filteredPosts;

  const paginatedGridPosts = gridPosts.slice(0, visibleCount);
  const hasMore = gridPosts.length > visibleCount;

  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedCategory("all");
    setVisibleCount(9);
  };

  return (
    <div className="blog-list-wrapper">
      {/* Controles de Busca e Filtros */}
      <div className="blog-controls">
        <div className="blog-search-box">
          <svg
            className="blog-search-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="blog-search-input"
            placeholder="Pesquisar por assunto, título ou palavra-chave..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setVisibleCount(9);
            }}
          />
          {searchQuery && (
            <button
              type="button"
              className="blog-search-clear"
              onClick={() => setSearchQuery("")}
              title="Limpar pesquisa"
            >
              ✕
            </button>
          )}
        </div>

        {/* Categorias / Pills */}
        <div className="blog-cat-bar" role="tablist" aria-label="Categorias de artigos">
          <button
            type="button"
            className={`blog-cat-pill ${selectedCategory === "all" ? "blog-cat-pill--active" : ""}`}
            onClick={() => {
              setSelectedCategory("all");
              setVisibleCount(9);
            }}
          >
            <span>Todos os Artigos</span>
            <span className="blog-cat-count">{categoryCounts.all || 0}</span>
          </button>

          {categories.map((cat) => {
            const count = categoryCounts[cat.slug] || 0;
            if (count === 0) return null;
            return (
              <button
                type="button"
                key={cat.id}
                className={`blog-cat-pill ${selectedCategory === cat.slug ? "blog-cat-pill--active" : ""}`}
                onClick={() => {
                  setSelectedCategory(cat.slug);
                  setVisibleCount(9);
                }}
              >
                <span>{cat.name}</span>
                <span className="blog-cat-count">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Hero Featured Post (quando não houver filtros ativos) */}
      {featuredPost && (
        <Link className="blog-card blog-card--hero reveal-tilt" href={`/blog/${featuredPost.slug}`}>
          {featuredPost.coverImage && (
            <div className="blog-card-img-wrap">
              <img
                src={featuredPost.coverImage}
                alt={featuredPost.title}
                className="blog-card-img"
                loading="eager"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.onerror = null;
                  target.src = "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=80";
                }}
              />
            </div>
          )}
          <div className="blog-card__hero-content" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div className="blog-card__top">
              <span className="blog-card__cat">
                ★ DESTAQUE · {featuredPost.category?.name ?? "Pauta Fiscal"}
              </span>
              <span className="blog-card__date">
                {fmtDate(featuredPost.publishedAt).toUpperCase()}
              </span>
            </div>
            <h2 className="blog-card__title">{featuredPost.title}</h2>
            {featuredPost.excerpt && <p className="blog-card__desc">{featuredPost.excerpt}</p>}
            <div className="blog-card__foot">
              <span>{featuredPost.author?.name ?? featuredPost.authorName ?? "Global ERP"}</span>
              {featuredPost.readingTime ? (
                <span>{featuredPost.readingTime} min de leitura</span>
              ) : null}
              <span className="blog-card__arrow">Ler Artigo Completo →</span>
            </div>
          </div>
        </Link>
      )}

      {/* Grid de Artigos */}
      {filteredPosts.length === 0 ? (
        <div className="blog-empty">
          <p
            className="mono"
            style={{
              fontSize: 12,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              marginBottom: 12,
              color: "var(--neon-aqua)",
            }}
          >
            {searchQuery ? "Nenhum resultado encontrado" : emptyState.eyebrow}
          </p>
          <p style={{ maxWidth: "48ch", margin: "0 auto 20px" }}>
            {searchQuery
              ? `Não encontramos nenhum artigo correspondente a "${searchQuery}". Tente pesquisar por outros termos ou limpar os filtros.`
              : emptyState.text}
          </p>
          {hasFilter && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={handleResetFilters}
            >
              Ver todos os artigos
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="blog-grid">
            {paginatedGridPosts.map((p) => (
              <Link className="blog-card reveal-tilt" href={`/blog/${p.slug}`} key={p.id}>
                {p.coverImage && (
                  <div className="blog-card-img-wrap">
                    <img
                      src={p.coverImage}
                      alt={p.title}
                      className="blog-card-img"
                      loading="lazy"
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.onerror = null;
                        target.src = "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=80";
                      }}
                    />
                  </div>
                )}
                <div className="blog-card__top">
                  {p.category ? (
                    <span className="blog-card__cat">{p.category.name}</span>
                  ) : (
                    <span className="blog-card__cat blog-card__cat--default">Pauta Fiscal</span>
                  )}
                  <span className="blog-card__date">
                    {p.featured ? (
                      <span title="Destaque" style={{ color: "var(--neon-aqua)", marginRight: 6 }}>
                        ★
                      </span>
                    ) : null}
                    {fmtDate(p.publishedAt).toUpperCase()}
                  </span>
                </div>
                <h3 className="blog-card__title">{p.title}</h3>
                {p.excerpt ? <p className="blog-card__desc">{p.excerpt}</p> : null}
                <div className="blog-card__foot">
                  <span>{p.author?.name ?? p.authorName ?? "Global ERP"}</span>
                  {p.readingTime ? <span>{p.readingTime} min de leitura</span> : null}
                  <span className="blog-card__arrow">→</span>
                </div>
              </Link>
            ))}
          </div>

          {/* Botão Carregar Mais */}
          {hasMore && (
            <div className="blog-load-more">
              <button
                type="button"
                className="btn btn--primary btn--lg btn-magnetic"
                onClick={() => setVisibleCount((prev) => prev + 9)}
              >
                Carregar mais artigos (+{gridPosts.length - visibleCount})
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
